/**
 * LTI 1.3 callback: backend redirects here with a one-time ?code= (never a session JWT).
 * The SPA POSTs the code to /lti/session, stores the JWT from the JSON body, then goes to the dashboard.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setStoredEmailToken } from '@/auth/emailAuth';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '';

function apiUrl(path: string): string {
  const base = API_BASE.replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

export default function LtiCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const legacyToken = searchParams.get('token');
  const error = searchParams.get('error');
  const [message, setMessage] = useState('Completing LTI sign-in…');

  useEffect(() => {
    if (error) {
      setMessage(error);
      return;
    }
    if (legacyToken) {
      setMessage('This LTI launch used an outdated callback. Ask your administrator to update SILISENSE, then retry from the LMS.');
      return;
    }
    if (!code) {
      navigate('/login', { replace: true });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(apiUrl('/lti/session'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        if (!res.ok) {
          throw new Error('LTI session exchange failed');
        }
        const data = (await res.json()) as { accessToken?: string; context?: unknown };
        if (cancelled) return;
        if (!data.accessToken) {
          navigate('/login', { replace: true });
          return;
        }
        setStoredEmailToken(data.accessToken);
        if (data.context != null) {
          try {
            sessionStorage.setItem('lti_context', JSON.stringify(data.context));
          } catch {
            /* non-fatal */
          }
        }
        navigate('/dashboard', { replace: true });
      } catch {
        if (!cancelled) setMessage('Could not complete LTI sign-in. Return to your LMS and try again.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, legacyToken, error, navigate]);

  return (
    <div className="login-page">
      <div className="login-card">
        <p className="page-muted">{message}</p>
        {error || legacyToken ? (
          <p className="page-muted">
            <a href="/login">Return to sign in</a>
          </p>
        ) : null}
      </div>
    </div>
  );
}
